/**
 * SSO Module — mesh-shared registry SSO providerů s per-node failoverem.
 *
 * Sekce:
 *  - Failover konfigurace (interval, promotion delay, timeout) — mesh-shared přes catalog
 *  - Per provider list instancí (Keycloak atd.) v PriorityList — per tenant scope
 *  - Credentials modal (per-node)
 *  - Active instance badge + health observation status
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import i18n from '../../lib/i18n';
import EduroamModule from './eduroam';
import { EntraOnboardingCard } from './sso-entra-wizard';
import { SsoMfaPanel } from './SsoMfaPanel';
import {
  AsyncStatusToggle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Modal,
  PageHeader,
  PriorityList,
  RefreshButton,
  Select,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import {
  Activity,
  CheckCircle2,
  Clock,
  ExternalLink,
  Key,
  Loader2,
  RefreshCw,
  Shield,
  ShieldCheck,
  TestTube2,
  Users,
  Wifi,
  WifiOff,
  XCircle, Palette } from 'lucide-react';
import { themedConfirm } from '../../lib/themed-dialogs';
import { callWidgetTool } from '../../widgets/scope';
import type { EffectiveScope } from '../../widgets/types';
import { ModuleScopeBar } from '../../widgets/ModuleScopeBar';

/** Node-aware MCP — stejný vzor jako backup / orchestrator. */
let _ssoScope: EffectiveScope = { node: null, identity: null };
function setSsoScope(scope: EffectiveScope) {
  _ssoScope = scope;
}
function mcpCall<T = unknown>(name: string, args: Record<string, unknown> = {}) {
  return callWidgetTool(name, args, _ssoScope) as Promise<T>;
}

function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

// ── Types ───────────────────────────────────────────────────────────────────

interface SsoInstance {
  id: string;
  provider: string;
  slug: string;
  label: string;
  url: string;
  priority: number;
  enabled: boolean;
  scope: string;
  meta: Record<string, unknown>;
  credentials?: { configured: boolean; mode?: 'password' | 'service_account' };
  updated_at?: string;
}

interface GlobalConfig {
  health_check_interval_sec: number;
  promotion_delay_sec: number;
  check_timeout_sec: number;
}

interface HealthObs {
  instance_id: string;
  node_id: string;
  ok: boolean;
  latency_ms: number;
  error?: string;
  checked_at: string;
}

interface ActiveInfo {
  instance_id: string | null;
  label: string | null;
  reason: string;
  credentials_configured: boolean;
}

interface Tenant {
  slug: string;
  name: string;
}

// ── Main ────────────────────────────────────────────────────────────────────

export default function SsoPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [node, setNode] = useState<string | null>(null);
  setSsoScope({ node, identity: null });
  const scopeKey = node ?? 'local';

  const instancesQ = useQuery<{ ok: boolean; my_scopes: string[]; instances: SsoInstance[] }>({
    queryKey: ['sso-instances', scopeKey],
    queryFn: () => mcpCall('sso.instances_list', {}),
    refetchInterval: 10_000,
  });

  const configQ = useQuery<{ ok: boolean; config: GlobalConfig }>({
    queryKey: ['sso-config', scopeKey],
    queryFn: () => mcpCall('sso.config_get', {}),
  });

  const activeQ = useQuery<
    { ok: boolean; node_id: string; my_tenant: string; active: Record<string, ActiveInfo> }
  >({
    queryKey: ['sso-active', scopeKey],
    queryFn: () => mcpCall('sso.active_all', {}),
    refetchInterval: 10_000,
  });

  const obsQ = useQuery<{ ok: boolean; observations: HealthObs[] }>({
    queryKey: ['sso-observations', scopeKey],
    queryFn: () => mcpCall('sso.observations_list', {}),
    refetchInterval: 10_000,
  });

  const tenantsQ = useQuery<{ ok: boolean; my_tenant: string; tenants: Tenant[] }>({
    queryKey: ['sso-tenants', scopeKey],
    queryFn: () => mcpCall('sso.list_tenants', {}),
  });

  const saveConfig = useMutation({
    mutationFn: (cfg: Partial<GlobalConfig>) => mcpCall('sso.config_set', cfg),
    onSuccess: () => {
      toast.success(t('sso.configSaved'));
      qc.invalidateQueries({ queryKey: ['sso-config'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const instances = asArray<SsoInstance>(instancesQ.data?.instances);
  const config = configQ.data?.config;
  const active = activeQ.data?.active ?? {};
  const observations = asArray<HealthObs>(obsQ.data?.observations);
  const tenants = asArray<Tenant>(tenantsQ.data?.tenants);
  const myTenant = tenantsQ.data?.my_tenant ?? 'global';

  // Seskup instance podle provider
  const byProvider = useMemo(() => {
    const map = new Map<string, SsoInstance[]>();
    for (const i of instances) {
      if (!map.has(i.provider)) map.set(i.provider, []);
      map.get(i.provider)!.push(i);
    }
    return map;
  }, [instances]);

  return (
    <div className='space-y-6'>
      <ModuleScopeBar
        node={node}
        onNodeChange={(n) => {
          setNode(n);
          setSsoScope({ node: n, identity: null });
          qc.invalidateQueries({ queryKey: ['sso-instances'] });
          qc.invalidateQueries({ queryKey: ['sso-config'] });
          qc.invalidateQueries({ queryKey: ['sso-active'] });
          qc.invalidateQueries({ queryKey: ['sso-observations'] });
          qc.invalidateQueries({ queryKey: ['sso-tenants'] });
        }}
        onRefresh={async () => {
          await Promise.all([
            instancesQ.refetch(),
            configQ.refetch(),
            activeQ.refetch(),
            obsQ.refetch(),
            tenantsQ.refetch(),
          ]);
        }}
        refreshing={
          instancesQ.isFetching || configQ.isFetching || activeQ.isFetching ||
          obsQ.isFetching || tenantsQ.isFetching
        }
      />

      <PageHeader
        title='SSO'
        description={t('sso.pageDescription', {
          node: node ?? activeQ.data?.node_id ?? 'local',
          tenant: myTenant,
        })}
        icon={Shield}
      />

      <Tabs defaultTab='instances'>
        <TabList>
          <Tab id='instances'>{t('sso.tabInstances')}</Tab>
          <Tab id='identity'>{t('sso.tabIdentity')}</Tab>
          <Tab id='oauth' icon={<Key size={13} />}>{t('sso.tabOauth')}</Tab>
          <Tab id='clients'>{t('sso.tabKcClients')}</Tab>
          <Tab id='registrations'>{t('sso.tabRegistrations')}</Tab>
          <Tab id='scopes'>{t('sso.tabScopes')}</Tab>
          <Tab id='config'>{t('sso.tabFailover')}</Tab>
          <Tab id='branding' icon={<Palette size={13} />}>{t('sso.tabBranding')}</Tab>
          <Tab id='mfa' icon={<ShieldCheck size={13} />}>{t('sso.tabMfa')}</Tab>
          <Tab id='observations'>{t('sso.tabObservations')}</Tab>
          <Tab id='eduroam' icon={<Wifi size={13} />}>{t('sso.tabEduroam')}</Tab>
        </TabList>

        <TabPanel id='instances'>
          <div className='space-y-4'>
            {[...byProvider.entries()].map(([provider, insts]) => (
              <ProviderSection
                key={provider}
                provider={provider}
                instances={insts}
                active={active[provider]}
                observations={observations}
                tenants={tenants}
                myTenant={myTenant}
                onChange={() => qc.invalidateQueries({ queryKey: ['sso-instances'] })}
              />
            ))}
            {byProvider.size === 0 && (
              <Card>
                <CardContent className='py-10 text-center text-gray-500'>
                  <Shield size={32} className='mx-auto mb-3 opacity-50' />
                  {t('sso.noInstances')}
                  <div className='mt-4'>
                    <AddFirstKeycloakButton
                      tenants={tenants}
                      myTenant={myTenant}
                      onAdded={() => qc.invalidateQueries({ queryKey: ['sso-instances'] })}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabPanel>

        <TabPanel id='oauth'>
          <McpOauthTab />
        </TabPanel>

        <TabPanel id='clients'>
          <KcClientsTab />
        </TabPanel>

        <TabPanel id='registrations'>
          <RegistrationsTab />
        </TabPanel>

        <TabPanel id='identity'>
          <IdentityTab />
        </TabPanel>

        <TabPanel id='scopes'>
          <ScopesPanel
            tenants={tenants}
            myTenant={myTenant}
            onChange={() => qc.invalidateQueries({ queryKey: ['sso-tenants'] })}
          />
        </TabPanel>

        <TabPanel id='config'>
          <ConfigPanel
            config={config}
            onSave={(cfg) => saveConfig.mutate(cfg)}
            saving={saveConfig.isPending}
          />
        </TabPanel>

        <TabPanel id='branding'>
          <BrandingPanel />
        </TabPanel>

        <TabPanel id='mfa'>
          <SsoMfaPanel />
        </TabPanel>

        <TabPanel id='observations'>
          <ObservationsPanel observations={observations} instances={instances} />
        </TabPanel>

        <TabPanel id='eduroam'>
          <EduroamModule embedded />
        </TabPanel>
      </Tabs>
    </div>
  );
}

// ── Provider section — PriorityList per provider ─────────────────────────────

function ProviderSection({
  provider,
  instances,
  active,
  observations,
  tenants,
  myTenant,
  onChange,
}: {
  provider: string;
  instances: SsoInstance[];
  active?: ActiveInfo;
  observations: HealthObs[];
  tenants: Tenant[];
  myTenant: string;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const [secretModalId, setSecretModalId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Record<string, { ok: boolean; latency_ms: number; error?: string }>
  >({});

  const upsert = useMutation({
    mutationFn: (inst: Partial<SsoInstance>) =>
      mcpCall<{ ok: boolean; error?: string }>('sso.instance_upsert', inst),
    onSuccess: (r) => {
      // ok:false je legitimní odpověď (server-side validace, viz
      // sso_instance_upsert enable-gate) — mcpCall() nehodí throw, jen vrátí
      // payload. Bez tyhle kontroly by "Uloženo" naskočilo i po odmítnutí.
      if (!r.ok) {
        toast.error(r.error ?? t('sso.saveFailed'));
        return;
      }
      toast.success(t('sso.saved'));
      onChange();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (id: string) => mcpCall('sso.instance_delete', { id }),
    onSuccess: () => {
      toast.success(t('sso.removed'));
      onChange();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => mcpCall('sso.instances_reorder', { ids }),
    onSuccess: () => onChange(),
    onError: (e: Error) => toast.error(e.message),
  });

  async function testInstance(id: string) {
    try {
      const r = await mcpCall<{ ok: boolean; latency_ms: number; error?: string }>(
        'sso.test_instance',
        { instance_id: id },
      );
      setTestResults((p) => ({
        ...p,
        [id]: { ok: r.ok, latency_ms: r.latency_ms, error: r.error },
      }));
      if (r.ok) toast.success(`${id}: ${r.latency_ms}ms`);
      else toast.error(`${id}: ${r.error}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setTestResults((p) => ({ ...p, [id]: { ok: false, latency_ms: 0, error: msg } }));
      toast.error(msg);
    }
  }

  function addNew() {
    const slug = `instance-${Date.now()}`;
    upsert.mutate({
      provider,
      slug,
      id: `${provider}:${slug}`,
      label: t('sso.newInstanceLabel', { provider }),
      url: 'https://',
      priority: instances.length + 1,
      enabled: false,
      scope: myTenant === 'global' ? 'global' : myTenant,
      meta: provider === 'keycloak'
        ? { realm: 'master', client_id: 'admin-cli', username: 'admin' }
        : {},
    });
  }

  // Latest observation per instance
  const lastObs = new Map<string, HealthObs>();
  for (const o of observations) {
    const cur = lastObs.get(o.instance_id);
    if (!cur || new Date(o.checked_at).getTime() > new Date(cur.checked_at).getTime()) {
      lastObs.set(o.instance_id, o);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Shield}>
          <span className='capitalize'>{provider}</span>
          <Badge variant='info' className='ml-2'>{instances.length}</Badge>
          {active?.instance_id && (
            <span className='ml-3 text-xs text-emerald-400 font-normal'>
              {t('sso.activeLabel', { label: active.label })}
            </span>
          )}
        </CardTitle>
        <span className='text-[11px] text-gray-500'>{active?.reason}</span>
      </CardHeader>
      <CardContent>
        <PriorityList<SsoInstance>
          items={instances.sort((a, b) => a.priority - b.priority)}
          getTitle={(i) => i.label}
          getSubtitle={(i) => (
            <span className='font-mono'>
              {i.url} · scope:{' '}
              <span className={i.scope === 'global' ? 'text-blue-400' : 'text-amber-400'}>
                {i.scope}
              </span>
              {i.meta?.realm ? ` · realm: ${String(i.meta.realm)}` : ''}
            </span>
          )}
          renderEditor={(item, update) => (
            <div className='grid grid-cols-2 gap-3'>
              <Field label={t('sso.labelField')} value={item.label} onChange={(v) => update({ label: v })} />
              <Field label={t('sso.urlField')} value={item.url} onChange={(v) => update({ url: v })} mono />
              <div>
                <Label>{t('sso.scopeVisibilityLabel')}</Label>
                <Select
                  value={item.scope}
                  onChange={(e) => update({ scope: e.target.value })}
                  options={tenants.map((tn) => ({
                    value: tn.slug,
                    label: `${tn.slug}${tn.slug === 'global' ? ` (${t('sso.everyone')})` : ''}`,
                  }))}
                />
              </div>
              <Field
                label={t('sso.priorityField')}
                value={String(item.priority)}
                onChange={(v) => update({ priority: Number(v) || 1 })}
              />
              {item.provider === 'keycloak' && (
                <>
                  <Field
                    label={t('sso.realmField')}
                    value={String(item.meta.realm ?? '')}
                    onChange={(v) => update({ meta: { ...item.meta, realm: v } })}
                  />
                  <Field
                    label={t('sso.adminClientIdField')}
                    value={String(item.meta.client_id ?? '')}
                    onChange={(v) => update({ meta: { ...item.meta, client_id: v } })}
                  />
                  <Field
                    label={t('sso.usernameField')}
                    value={String(item.meta.username ?? '')}
                    onChange={(v) => update({ meta: { ...item.meta, username: v } })}
                  />
                </>
              )}
              <div className='col-span-2 text-[11px] text-gray-500 border-t border-gray-800 pt-2'>
                {t('sso.credentialsNote1')}{' '}
                <Key size={10} className='inline' /> {t('sso.credentialsNote2')}
              </div>
            </div>
          )}
          renderBadges={(i) => {
            const obs = lastObs.get(i.id);
            const tr = testResults[i.id];
            const status = tr ?? obs;
            return (
              <>
                <span className='text-[10px] font-mono text-gray-500'>P{i.priority}</span>
                {i.credentials?.configured
                  ? <Badge variant='success' className='text-[10px]'>{t('sso.credsBadge')}</Badge>
                  : <Badge variant='warning' className='text-[10px]'>{t('sso.noCredsBadge')}</Badge>}
                {status && (
                  status.ok
                    ? <CheckCircle2 size={14} className='text-emerald-400' />
                    : <XCircle size={14} className='text-red-400' />
                )}
                {active?.instance_id === i.id && (
                  <Badge variant='info' className='text-[10px]'>{t('sso.activeBadge')}</Badge>
                )}
              </>
            );
          }}
          extraActions={(i) => (
            <>
              <button
                onClick={() => testInstance(i.id)}
                title={t('sso.testConnectivityTooltip')}
                className='p-1.5 rounded hover:bg-gray-700 text-gray-500 hover:text-blue-400'
              >
                <TestTube2 size={13} />
              </button>
              <button
                onClick={() => setSecretModalId(i.id)}
                title={t('sso.perNodeCredentialsTooltip')}
                className='p-1.5 rounded hover:bg-gray-700 text-gray-500 hover:text-yellow-400'
              >
                <Key size={13} />
              </button>
            </>
          )}
          onSave={(i) => upsert.mutate(i)}
          onDelete={(id) => del.mutate(id)}
          onToggle={(id, enabled) => {
            const i = instances.find((x) => x.id === id);
            if (!i) return;
            // Zapnutí bez validní konfigurace = rozbitá instance v aktivní
            // failover rotaci (Filip 2026-07-31: "jde zapnout i když nemá
            // konfiguraci"). URL 'https://' je placeholder z addNew(), ne
            // reálná hodnota.
            if (enabled) {
              const urlOk = !!i.url && i.url !== 'https://' && i.url.length > 'https://'.length;
              const kcOk = i.provider !== 'keycloak' || (!!i.meta?.realm && !!i.meta?.client_id);
              if (!urlOk || !kcOk) {
                toast.error(t('sso.enableBlockedIncomplete'));
                // Toggle dělá optimistický UI flip a vrací ho zpět JEN při
                // thrownuté chybě (viz Toggle.tsx handleClick catch) — prosté
                // `return` by přepínač nechalo vizuálně "zapnutý" i po
                // odmítnutí (přesně Filipův report: hláška padla, přepínač
                // zůstal ON).
                throw new Error(t('sso.enableBlockedIncomplete'));
              }
            }
            upsert.mutate({ ...i, enabled });
          }}
          onReorder={(ids) => reorder.mutate(ids)}
          onAdd={addNew}
          addLabel={t('sso.addInstance', { provider })}
          emptyMessage={t('sso.noProviderInstance', { provider })}
        />
      </CardContent>

      {secretModalId && (
        <SecretModal
          instanceId={secretModalId}
          provider={provider}
          onClose={() => setSecretModalId(null)}
          onSaved={onChange}
        />
      )}
    </Card>
  );
}

// ── Secret modal (per-node credentials) ──────────────────────────────────────

interface AcmNodeBrief {
  id: string;
  label: string;
  host?: string;
  is_local?: boolean;
}

function SecretModal({
  instanceId,
  provider,
  onClose,
  onSaved,
}: {
  instanceId: string;
  provider: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'password' | 'service_account' | 'token'>('password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [token, setToken] = useState('');
  const [scope, setScope] = useState<string[]>(['local']);

  // Mesh nody pro scope select (peer ACM instance kam šířit credentials)
  const { data: nodesData } = useQuery({
    queryKey: ['acm-nodes-brief'],
    queryFn: () =>
      mcpCall<{ ok: boolean; nodes?: AcmNodeBrief[] }>('mesh.peers_list', {})
        .catch(() => ({ ok: false, nodes: [] as AcmNodeBrief[] })),
  });
  const peerNodes = (nodesData?.nodes ?? []).filter((n) => !n.is_local);

  const save = useMutation({
    mutationFn: () =>
      mcpCall<{ ok: boolean; warnings?: string[] }>(
        'sso.set_secret',
        {
          instance_id: instanceId,
          mode,
          scope,
          ...(mode === 'password' ? { username, password } : {}),
          ...(mode === 'service_account'
            ? { client_id: clientId, client_secret: clientSecret }
            : {}),
          ...(mode === 'token' ? { token } : {}),
        },
      ),
    onSuccess: (r) => {
      toast.success(t('sso.credentialsSaved'));
      if (r.warnings?.length) { for (const w of r.warnings) toast.warning(w, { duration: 8000 }); }
      onSaved();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleScope = (id: string) => {
    setScope((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  return (
    <Modal open={true} onClose={onClose} title={t('sso.credentialsModalTitle', { id: instanceId })}>
      <div className='space-y-4'>
        <div>
          <Label>{t('sso.credentialsTypeLabel')}</Label>
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value as typeof mode)}
            options={[
              { value: 'password', label: t('sso.modePassword') },
              { value: 'service_account', label: t('sso.modeServiceAccount') },
              { value: 'token', label: t('sso.modeToken') },
            ]}
          />
        </div>

        {mode === 'password' && (
          <>
            <Field label={t('sso.usernameField')} value={username} onChange={setUsername} />
            <Field label={t('sso.passwordField')} value={password} onChange={setPassword} type='password' />
          </>
        )}
        {mode === 'service_account' && (
          <>
            <Field label={t('sso.clientIdField')} value={clientId} onChange={setClientId} mono />
            <Field
              label={t('sso.clientSecretField')}
              value={clientSecret}
              onChange={setClientSecret}
              type='password'
              mono
            />
            {provider === 'keycloak' && (
              <div className='text-[11px] text-gray-500'>
                {t('sso.saHint1')} <code>confidential</code> {t('sso.saHint2')}{' '}
                <code>service_accounts_enabled</code>{t('sso.saHint3')}
              </div>
            )}
          </>
        )}
        {mode === 'token' && (
          <>
            <Field label={t('sso.bearerTokenField')} value={token} onChange={setToken} type='password' mono />
            {provider === 'keycloak' && (
              <div className='text-[11px] text-gray-500'>
                {t('sso.tokenHint1')} <code>offline_access</code>{' '}
                {t('sso.tokenHint2')}
              </div>
            )}
          </>
        )}

        {/* Scope — kam šířit credentials */}
        <div>
          <Label>{t('sso.credentialsScopeLabel')}</Label>
          <div className='space-y-1.5 bg-gray-900 border border-gray-800 rounded p-2 max-h-40 overflow-y-auto'>
            <label className='flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-800 px-2 py-1 rounded'>
              <input
                type='checkbox'
                checked={scope.includes('local')}
                onChange={() => toggleScope('local')}
              />
              <span className='text-white'>{t('sso.thisNode')}</span>
              <span className='text-gray-600'>(local)</span>
            </label>
            {peerNodes.map((n) => (
              <label
                key={n.id}
                className='flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-800 px-2 py-1 rounded'
              >
                <input
                  type='checkbox'
                  checked={scope.includes(n.id)}
                  onChange={() => toggleScope(n.id)}
                />
                <span className='text-white'>{n.label}</span>
                <span className='text-gray-600 font-mono'>{n.host}</span>
              </label>
            ))}
            {peerNodes.length === 0 && (
              <div className='text-[11px] text-gray-600 px-2 py-1'>
                {t('sso.noPeerNodes')}
              </div>
            )}
          </div>
          {scope.some((s) => s !== 'local') && (
            <div className='text-[11px] text-amber-400 mt-1'>
              {t('sso.multiNodeWarning')}
            </div>
          )}
        </div>

        <div className='flex gap-2 justify-end pt-2 border-t border-gray-800'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant='primary' onClick={() => save.mutate()} loading={save.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Scopes panel — CRUD pro mesh tenanty ─────────────────────────────────────

function ScopesPanel({ tenants, myTenant, onChange }: {
  tenants: Tenant[];
  myTenant: string;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const [newSlug, setNewSlug] = useState('');
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ slug: string; name: string } | null>(null);

  const create = useMutation({
    mutationFn: () => mcpCall('sso.tenant_create', { slug: newSlug, name: newName }),
    onSuccess: () => {
      toast.success(t('sso.scopeCreated', { slug: newSlug }));
      setNewSlug('');
      setNewName('');
      onChange();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const update = useMutation({
    mutationFn: (p: { slug: string; name: string }) =>
      mcpCall('sso.tenant_update', { slug: p.slug, name: p.name }),
    onSuccess: () => {
      toast.success(t('sso.scopeUpdated'));
      setEditing(null);
      onChange();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: ({ slug, force }: { slug: string; force: boolean }) =>
      mcpCall<{ ok: boolean; error?: string; usage?: { nodes: number; sso_configs: number } }>(
        'sso.tenant_delete',
        { slug, force },
      ),
    onSuccess: async (r, vars) => {
      if (r.ok) {
        toast.success(t('sso.scopeDeleted', { slug: vars.slug }));
        onChange();
      } else if (r.usage) {
        const ok = await themedConfirm(
          t('sso.confirmForceDelete', {
            slug: vars.slug,
            nodes: r.usage.nodes,
            configs: r.usage.sso_configs,
          }),
        );
        if (ok) remove.mutate({ slug: vars.slug, force: true });
      } else {
        toast.error(r.error ?? t('sso.deleteFailed'));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deletable = tenants.filter((tn) => tn.slug !== 'global');

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={Shield}>{t('sso.meshScopesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='text-xs text-gray-500 mb-3'>
            {t('sso.scopesIntro1')} <code>global</code>
            {' '}
            {t('sso.scopesIntro2')} <Badge variant='info'>{myTenant}</Badge>
          </div>

          {/* Desktop table */}
          <div className='hidden md:block overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead className='text-xs text-gray-500 border-b border-gray-800'>
                <tr>
                  <th className='text-left py-2'>{t('sso.colSlug')}</th>
                  <th className='text-left'>{t('sso.colName')}</th>
                  <th className='text-left'>{t('sso.colPlan')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tn) => (
                  <tr key={tn.slug} className='border-b border-gray-800/40'>
                    <td className='py-1.5 font-mono text-white'>
                      {tn.slug}
                      {tn.slug === myTenant && (
                        <Badge variant='info' className='ml-2 text-[10px]'>{t('sso.myBadge')}</Badge>
                      )}
                    </td>
                    <td>
                      {editing?.slug === tn.slug
                        ? (
                          <input
                            autoFocus
                            className='bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs w-full'
                            value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') update.mutate(editing);
                              if (e.key === 'Escape') setEditing(null);
                            }}
                          />
                        )
                        : <span className='text-gray-300'>{tn.name}</span>}
                    </td>
                    <td className='text-gray-500 text-xs'>{(tn as { plan?: string }).plan ?? '—'}</td>
                    <td className='text-right'>
                      {tn.slug === 'global'
                        ? <span className='text-[10px] text-gray-700'>{t('sso.reservedLabel')}</span>
                        : editing?.slug === tn.slug
                        ? (
                          <>
                            <Button variant='ghost' size='sm' onClick={() => setEditing(null)}>
                              {t('common.cancel')}
                            </Button>
                            <Button
                              variant='primary'
                              size='sm'
                              loading={update.isPending}
                              onClick={() => update.mutate(editing)}
                            >
                              {t('common.save')}
                            </Button>
                          </>
                        )
                        : (
                          <>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => setEditing({ slug: tn.slug, name: tn.name })}
                            >
                              {t('sso.edit')}
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => remove.mutate({ slug: tn.slug, force: false })}
                            >
                              {t('sso.delete')}
                            </Button>
                          </>
                        )}
                    </td>
                  </tr>
                ))}
                {deletable.length === 0 && (
                  <tr>
                    <td colSpan={4} className='text-center text-gray-600 py-4'>
                      {t('sso.noCustomScopes')} <code>global</code>.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Phone cards */}
          <div className='md:hidden space-y-2'>
            {tenants.map((tn) => (
              <div
                key={tn.slug}
                className='rounded-xl border border-gray-800 bg-gray-950/40 p-3 space-y-2'
              >
                <div className='flex items-center gap-2 flex-wrap'>
                  <span className='font-mono text-white text-sm'>{tn.slug}</span>
                  {tn.slug === myTenant && (
                    <Badge variant='info' className='text-[10px]'>{t('sso.myBadge')}</Badge>
                  )}
                  <span className='text-[11px] text-gray-500 ml-auto'>
                    {(tn as { plan?: string }).plan ?? '—'}
                  </span>
                </div>
                {editing?.slug === tn.slug
                  ? (
                    <input
                      autoFocus
                      className='bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-xs w-full'
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    />
                  )
                  : <div className='text-sm text-gray-300'>{tn.name}</div>}
                {tn.slug !== 'global' && (
                  <div className='flex gap-2 pt-1'>
                    {editing?.slug === tn.slug
                      ? (
                        <>
                          <Button variant='ghost' size='sm' onClick={() => setEditing(null)}>
                            {t('common.cancel')}
                          </Button>
                          <Button
                            variant='primary'
                            size='sm'
                            loading={update.isPending}
                            onClick={() => update.mutate(editing)}
                          >
                            {t('common.save')}
                          </Button>
                        </>
                      )
                      : (
                        <>
                          <Button
                            variant='secondary'
                            size='sm'
                            onClick={() => setEditing({ slug: tn.slug, name: tn.name })}
                          >
                            {t('sso.edit')}
                          </Button>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => remove.mutate({ slug: tn.slug, force: false })}
                          >
                            {t('sso.delete')}
                          </Button>
                        </>
                      )}
                  </div>
                )}
              </div>
            ))}
            {deletable.length === 0 && (
              <div className='text-center text-gray-600 py-4 text-sm'>
                {t('sso.noCustomScopes')} <code>global</code>.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('sso.addScopeTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 items-end'>
            <Field label={t('sso.colSlug')} value={newSlug} onChange={setNewSlug} mono />
            <Field label={t('sso.colName')} value={newName} onChange={setNewName} />
            <Button variant='primary' loading={create.isPending} onClick={() => create.mutate()}>
              {t('sso.createButton')}
            </Button>
          </div>
          <div className='text-[11px] text-gray-500 mt-2'>
            {t('sso.slugHint')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Config panel (global failover params) ────────────────────────────────────

// ── Branding přihlašovací stránky per realm (sso.realm_branding_get/set) ──────
type BrandForm = {
  display_name: string; tagline: string; info: string; footer: string;
  logo_url: string; favicon_url: string; font_family: string; hide_platform_logo: boolean;
  primary: string; primary_hover: string; background: string; card: string; text: string;
};
const BRAND_DEFAULT: BrandForm = {
  display_name: '', tagline: '', info: '', footer: '', logo_url: '', favicon_url: '', font_family: '',
  hide_platform_logo: true, primary: '#3b82f6', primary_hover: '', background: '#0a0a0a', card: '#111111', text: '#dddddd',
};

function BrandingPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['sso-realm-branding'],
    queryFn: () =>
      mcpCall<{
        ok: boolean; instance_id: string; realm: string; display_name: string | null; login_theme: string | null;
        branded: boolean; login_url: string;
        brand: (Partial<BrandForm> & { colors?: Partial<Record<'primary' | 'primary_hover' | 'background' | 'card' | 'text', string>> }) | null;
      }>('sso.realm_branding_get', {}),
  });
  const [form, setForm] = useState<BrandForm>(BRAND_DEFAULT);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  useMemo(() => {
    if (!data || loadedFor === data.realm) return;
    const b = data.brand;
    setForm({
      ...BRAND_DEFAULT,
      display_name: b?.display_name ?? data.display_name ?? '',
      tagline: b?.tagline ?? '', info: b?.info ?? '', footer: b?.footer ?? '',
      logo_url: b?.logo_url ?? '', favicon_url: b?.favicon_url ?? '', font_family: b?.font_family ?? '',
      hide_platform_logo: b?.hide_platform_logo ?? true,
      primary: b?.colors?.primary ?? BRAND_DEFAULT.primary,
      primary_hover: b?.colors?.primary_hover ?? '',
      background: b?.colors?.background ?? BRAND_DEFAULT.background,
      card: b?.colors?.card ?? BRAND_DEFAULT.card,
      text: b?.colors?.text ?? BRAND_DEFAULT.text,
    });
    setLoadedFor(data.realm);
  }, [data, loadedFor]);
  const set = (k: keyof BrandForm, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: (restart: 'auto' | 'always') =>
      mcpCall<{ ok: boolean; error?: string; theme?: string; restarted?: boolean; note?: string; login_url?: string }>(
        'sso.realm_branding_set',
        {
          restart,
          brand: {
            display_name: form.display_name, tagline: form.tagline || undefined, info: form.info || undefined,
            footer: form.footer || undefined, logo_url: form.logo_url || undefined, favicon_url: form.favicon_url || undefined,
            font_family: form.font_family || undefined, hide_platform_logo: form.hide_platform_logo,
            colors: {
              primary: form.primary, primary_hover: form.primary_hover || undefined,
              background: form.background, card: form.card, text: form.text,
            },
          },
        },
      ),
    onSuccess: (r) => {
      if (r.ok) toast.success(r.restarted ? t('sso.brandingSavedRestarted') : t('sso.brandingSaved'));
      else toast.error(r.error ?? t('sso.brandingFailed'));
      qc.invalidateQueries({ queryKey: ['sso-realm-branding'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <Card><CardContent>{t('common.loading')}</CardContent></Card>;

  const colorField = (k: 'primary' | 'primary_hover' | 'background' | 'card' | 'text', label: string) => (
    <div>
      <Label>{label}</Label>
      <div className='flex items-center gap-2'>
        <Input type='color' value={form[k] || '#000000'} onChange={(e) => set(k, e.target.value)} className='w-12 p-0.5' />
        <Input value={form[k]} placeholder='#rrggbb' onChange={(e) => set(k, e.target.value)} />
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between gap-2 flex-wrap'>
          <CardTitle icon={Palette}>{t('sso.brandingTitle')}</CardTitle>
          <div className='flex items-center gap-2'>
            {data && (
              <Badge variant={data.branded ? 'success' : 'default'}>
                {data.branded ? t('sso.brandingActive', { theme: data.login_theme }) : t('sso.brandingDefault')}
              </Badge>
            )}
            {data?.login_url && (
              <a href={data.login_url} target='_blank' rel='noopener noreferrer' className='text-xs text-blue-400 hover:text-blue-300'>
                {t('sso.brandingPreview')}
              </a>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className='text-xs text-gray-500 mb-3'>
          {t('sso.brandingHint', { realm: data?.realm ?? '', instance: data?.instance_id ?? '' })}
        </p>
        <div className='grid gap-3 md:grid-cols-2'>
          <div><Label>{t('sso.brandingName')}</Label><Input value={form.display_name} onChange={(e) => set('display_name', e.target.value)} /></div>
          <div><Label>{t('sso.brandingTagline')}</Label><Input value={form.tagline} onChange={(e) => set('tagline', e.target.value)} /></div>
          <div><Label>{t('sso.brandingLogoUrl')}</Label><Input value={form.logo_url} placeholder='https://…/logo.svg' onChange={(e) => set('logo_url', e.target.value)} /></div>
          <div><Label>{t('sso.brandingFaviconUrl')}</Label><Input value={form.favicon_url} placeholder='https://…/favicon.png' onChange={(e) => set('favicon_url', e.target.value)} /></div>
          <div className='md:col-span-2'><Label>{t('sso.brandingInfo')}</Label><Input value={form.info} onChange={(e) => set('info', e.target.value)} /></div>
          <div><Label>{t('sso.brandingFooter')}</Label><Input value={form.footer} onChange={(e) => set('footer', e.target.value)} /></div>
          <div><Label>{t('sso.brandingFont')}</Label><Input value={form.font_family} placeholder='"Montserrat", Arial, sans-serif' onChange={(e) => set('font_family', e.target.value)} /></div>
          {colorField('primary', t('sso.brandingColorPrimary'))}
          {colorField('primary_hover', t('sso.brandingColorPrimaryHover'))}
          {colorField('background', t('sso.brandingColorBackground'))}
          {colorField('card', t('sso.brandingColorCard'))}
          {colorField('text', t('sso.brandingColorText'))}
          <div className='flex items-center justify-between py-2'>
            <span className='text-sm text-white'>{t('sso.brandingHidePlatformLogo')}</span>
            <input type='checkbox' checked={form.hide_platform_logo} onChange={(e) => set('hide_platform_logo', e.target.checked)} className='w-4 h-4' />
          </div>
        </div>
        <div className='flex gap-2 mt-4'>
          <Button variant='primary' size='sm' loading={save.isPending} onClick={() => save.mutate('auto')}>
            {t('sso.brandingApply')}
          </Button>
          <Button variant='secondary' size='sm' loading={save.isPending} onClick={() => save.mutate('always')}>
            {t('sso.brandingApplyRestart')}
          </Button>
        </div>
        <p className='text-[10px] text-gray-600 mt-3'>{t('sso.brandingRestartHint')}</p>
      </CardContent>
    </Card>
  );
}

function ConfigPanel({ config, onSave, saving }: {
  config: GlobalConfig | undefined;
  onSave: (cfg: Partial<GlobalConfig>) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [interval, setInterval] = useState(config?.health_check_interval_sec ?? 60);
  const [delay, setDelay] = useState(config?.promotion_delay_sec ?? 300);
  const [timeout, setTimeout] = useState(config?.check_timeout_sec ?? 5);

  // Sync when config loads
  useMemo(() => {
    if (config) {
      setInterval(config.health_check_interval_sec);
      setDelay(config.promotion_delay_sec);
      setTimeout(config.check_timeout_sec);
    }
  }, [config]);

  if (!config) {
    return (
      <Card>
        <CardContent className='py-6 text-center'>
          <Loader2 className='animate-spin mx-auto' />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Clock}>{t('sso.failoverConfigTitle')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='text-xs text-gray-500 bg-blue-500/10 border border-blue-500/20 p-3 rounded'>
          {t('sso.failoverConfigNote')}
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          <div>
            <Label>{t('sso.healthCheckIntervalLabel')}</Label>
            <Input
              type='number'
              value={String(interval)}
              onChange={(e) => setInterval(Number(e.target.value))}
              min='30'
            />
            <div className='text-[11px] text-gray-500 mt-1'>{t('sso.healthCheckIntervalHint')}</div>
          </div>
          <div>
            <Label>{t('sso.promotionDelayLabel')}</Label>
            <Input
              type='number'
              value={String(delay)}
              onChange={(e) => setDelay(Number(e.target.value))}
              min='0'
            />
            <div className='text-[11px] text-gray-500 mt-1'>
              {t('sso.promotionDelayHint')}
            </div>
          </div>
          <div>
            <Label>{t('sso.checkTimeoutLabel')}</Label>
            <Input
              type='number'
              value={String(timeout)}
              onChange={(e) => setTimeout(Number(e.target.value))}
              min='1'
              max='60'
            />
            <div className='text-[11px] text-gray-500 mt-1'>{t('sso.checkTimeoutHint')}</div>
          </div>
        </div>
        <div className='flex justify-end'>
          <Button
            variant='primary'
            onClick={() =>
              onSave({
                health_check_interval_sec: interval,
                promotion_delay_sec: delay,
                check_timeout_sec: timeout,
              })}
            loading={saving}
          >
            {t('sso.saveConfigButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Observations panel ───────────────────────────────────────────────────────

function ObservationsPanel({ observations, instances }: {
  observations: HealthObs[];
  instances: SsoInstance[];
}) {
  const { t } = useTranslation();
  // Matrix: instance × node_id
  const byInstance = new Map<string, Map<string, HealthObs>>();
  for (const o of observations) {
    if (!byInstance.has(o.instance_id)) byInstance.set(o.instance_id, new Map());
    const existing = byInstance.get(o.instance_id)!.get(o.node_id);
    if (!existing || new Date(o.checked_at).getTime() > new Date(existing.checked_at).getTime()) {
      byInstance.get(o.instance_id)!.set(o.node_id, o);
    }
  }

  const allNodes = new Set<string>();
  for (const o of observations) allNodes.add(o.node_id);
  const nodes = [...allNodes].sort();

  if (observations.length === 0) {
    return (
      <Card>
        <CardContent className='py-10 text-center text-gray-500'>
          <Activity size={32} className='mx-auto mb-3 opacity-50' />
          {t('sso.noObservations')}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Activity}>{t('sso.observationsTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='overflow-x-auto -mx-1 px-1'>
          <table className='w-full text-sm min-w-[20rem]'>
            <thead>
              <tr className='text-gray-500 text-xs'>
                <th className='text-left py-2 px-2'>{t('sso.colInstance')}</th>
                {nodes.map((n) => <th key={n} className='px-2 font-mono'>{n}</th>)}
              </tr>
            </thead>
            <tbody>
              {instances.map((inst) => {
                const row = byInstance.get(inst.id);
                return (
                  <tr key={inst.id} className='border-t border-gray-800/50'>
                    <td className='py-2 px-2 font-mono text-white'>{inst.label}</td>
                    {nodes.map((n) => {
                      const obs = row?.get(n);
                      if (!obs) return <td key={n} className='px-2 text-gray-600 text-center'>—</td>;
                      const age = Math.floor(
                        (Date.now() - new Date(obs.checked_at).getTime()) / 1000,
                      );
                      return (
                        <td key={n} className='px-2 text-center'>
                          {obs.ok
                            ? (
                              <span className='text-emerald-400 inline-flex items-center gap-1'>
                                <Wifi size={12} />
                                {obs.latency_ms}ms
                              </span>
                            )
                            : (
                              <span
                                className='text-red-400 inline-flex items-center gap-1'
                                title={obs.error}
                              >
                                <WifiOff size={12} />
                                {age}s
                              </span>
                            )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── MCP OAuth (Grok/Claude) — veřejný PKCE klient v aktivním Keycloaku ──────

interface McpOauthModuleRow {
  id: string;
  label: string;
  enabled?: boolean;
  allowed?: boolean;
}

interface McpOauthDesc {
  ok?: boolean;
  error?: string;
  platform_enabled?: boolean;
  modules?: Record<string, boolean>;
  module_catalog?: McpOauthModuleRow[];
  client_id?: string;
  issuer?: string;
  client_exists?: boolean;
  client_enabled?: boolean;
  client_ok?: boolean;
  client_error?: string;
}

function McpOauthTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['sso-mcp-oauth'],
    queryFn: () => mcpCall<McpOauthDesc>('sso.mcp_oauth', { action: 'describe' }),
    staleTime: 15_000,
  });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['sso-mcp-oauth'] });
    await qc.invalidateQueries({ queryKey: ['sso-kc-clients'] });
    await qc.invalidateQueries({ queryKey: ['codable-project'] });
  };
  const setPlatform = async (enabled: boolean) => {
    const r = await mcpCall<McpOauthDesc>(
      'sso.mcp_oauth',
      { action: 'set_platform', enabled },
    );
    if (!r || r.ok === false) {
      throw new Error(String(r?.error || t('sso.mcpOauthEnableFailed')));
    }
    if (r.client_error) toast.error(String(r.client_error));
    else {
      toast.success(
        enabled ? t('sso.mcpOauthPlatformOn') : t('sso.mcpOauthPlatformOff'),
      );
    }
    await refresh();
    return r;
  };
  const setModule = async (moduleId: string, enabled: boolean) => {
    const r = await mcpCall<McpOauthDesc>(
      'sso.mcp_oauth',
      { action: 'set_module', module: moduleId, enabled },
    );
    if (!r || r.ok === false) {
      throw new Error(String(r?.error || t('sso.mcpOauthEnableFailed')));
    }
    toast.success(
      enabled
        ? t('sso.mcpOauthModuleOn', 'OAuth pro {{module}} je povolené', { module: moduleId })
        : t('sso.mcpOauthModuleOff', 'OAuth pro {{module}} je zakázané', { module: moduleId }),
    );
    await refresh();
    return r;
  };

  if (q.isLoading) {
    return (
      <div className='py-8 text-center text-gray-500'>
        <Loader2 size={16} className='animate-spin inline mr-2' />{t('common.loading')}
      </div>
    );
  }
  if (q.isError) {
    return (
      <Card>
        <CardContent className='py-8 text-center'>
          <XCircle size={24} className='text-red-400 mx-auto mb-2' />
          <p className='text-sm text-red-400'>
            {(q.error as Error)?.message ?? t('sso.kcUnavailable')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const on = q.data?.platform_enabled === true;
  const catalog = q.data?.module_catalog?.length
    ? q.data.module_catalog
    : [{ id: 'codable', label: 'Codable', enabled: q.data?.modules?.codable === true }];

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Key}>{t('sso.mcpOauthTitle')}</CardTitle>
        <RefreshButton onRefresh={() => q.refetch()} loading={q.isFetching} />
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-xs text-gray-400 leading-relaxed'>
          {t('sso.mcpOauthHint')}
        </p>
        <AsyncStatusToggle
          value={on}
          actual={on}
          label={t('sso.mcpOauthPlatformLabel')}
          onToggle={setPlatform}
          onError={(e) => toast.error(String((e as Error)?.message ?? e))}
        />
        <div className='rounded-xl border border-gray-800 bg-gray-950/40 divide-y divide-gray-800/60'>
          <div className='px-3 py-2 text-[11px] text-gray-500'>
            {t('sso.mcpOauthModulesTitle', 'Moduly')}
          </div>
          {catalog.map((mod) => (
            <div key={mod.id} className='px-3 py-2.5'>
              <AsyncStatusToggle
                value={mod.enabled === true}
                actual={mod.enabled === true}
                disabled={!on}
                label={mod.label}
                onToggle={(enabled) => setModule(mod.id, enabled)}
                onError={(e) => toast.error(String((e as Error)?.message ?? e))}
              />
              {!on && (
                <p className='mt-1 text-[11px] text-gray-600'>
                  {t('sso.mcpOauthNeedGeneral', 'Nejdřív povol OAuth.')}
                </p>
              )}
            </div>
          ))}
        </div>
        {(q.data?.client_error || q.data?.error) && (
          <p className='text-[11px] text-amber-300'>
            {q.data.client_error || q.data.error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── KC Clients Tab ───────────────────────────────────────────────────────────

function KcClientsTab() {
  const { t } = useTranslation();
  const { data: resp, isLoading, refetch } = useQuery({
    queryKey: ['sso-kc-clients'],
    queryFn: async () => {
      const activeResp = await mcpCall<{ ok: boolean; active: Record<string, ActiveInfo> }>(
        'sso.active_all',
        {},
      );
      const kc = activeResp?.active?.keycloak;
      if (!kc?.instance_id) {
        return { ok: false, clients: [], realm: '', error: t('sso.noActiveKcInstance') };
      }
      // Potřebujeme realm z instance
      const instResp = await mcpCall<{ ok: boolean; instances: SsoInstance[] }>(
        'sso.instances_list',
        {},
      );
      const inst = instResp?.instances?.find((i) => i.id === kc.instance_id);
      const realm = (inst?.meta?.realm as string) ?? 'master';
      const clientsResp = await mcpCall<{ ok: boolean; clients: Array<Record<string, unknown>> }>(
        'sso.keycloak_list_clients',
        { realm },
      );
      return { ok: true, clients: clientsResp?.clients ?? [], realm };
    },
    staleTime: 30_000,
  });

  const clients = resp?.clients ?? [];
  const acmClients = clients.filter((c) => (c.clientId as string)?.startsWith('acm-'));
  const otherClients = clients.filter((c) =>
    !(c.clientId as string)?.startsWith('acm-') && (c.clientId as string) !== 'account' &&
    (c.clientId as string) !== 'admin-cli'
  );

  if (isLoading) {
    return (
      <div className='py-8 text-center text-gray-500'>
        <Loader2 size={16} className='animate-spin inline mr-2' />{t('common.loading')}
      </div>
    );
  }
  if (!resp?.ok) {
    return (
      <Card>
        <CardContent className='py-8 text-center'>
          <XCircle size={24} className='text-red-400 mx-auto mb-2' />
          <p className='text-sm text-red-400'>{resp?.error ?? t('sso.kcUnavailable')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={ShieldCheck}>{t('sso.acmClientsTitle', { count: acmClients.length })}</CardTitle>
          <RefreshButton onRefresh={refetch} loading={isLoading} />
        </CardHeader>
        <CardContent>
          {acmClients.length === 0
            ? (
              <p className='text-xs text-gray-500 py-4 text-center'>
                {t('sso.noAcmClients')}
              </p>
            )
            : (
              <div className='divide-y divide-gray-800/30'>
                {acmClients.map((c) => (
                  <div key={c.id as string} className='py-2.5 flex items-center gap-3'>
                    <ShieldCheck size={14} className='text-blue-400 flex-shrink-0' />
                    <div className='flex-1 min-w-0'>
                      <span className='text-sm font-mono text-white'>{c.clientId as string}</span>
                      {!!c.rootUrl && (
                        <a
                          href={c.rootUrl as string}
                          target='_blank'
                          rel='noopener'
                          className='text-[10px] text-gray-500 hover:text-blue-400 ml-2 inline-flex items-center gap-0.5'
                        >
                          {c.rootUrl as string} <ExternalLink size={7} />
                        </a>
                      )}
                    </div>
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded ${
                        c.enabled
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-gray-800 text-gray-500'
                      }`}
                    >
                      {c.enabled ? t('sso.enabledBadge') : t('sso.disabledBadge')}
                    </span>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle icon={Users}>{t('sso.otherClientsTitle', { count: otherClients.length })}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='divide-y divide-gray-800/30 max-h-64 overflow-y-auto'>
            {otherClients.map((c) => (
              <div key={c.id as string} className='py-2 flex items-center gap-2'>
                <span className='text-xs font-mono text-gray-400'>{c.clientId as string}</span>
                {!!c.rootUrl && (
                  <span className='text-[10px] text-gray-600 ml-auto'>{c.rootUrl as string}</span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Registrations Tab (SSO reconciler view) ──────────────────────────────────

function RegistrationsTab() {
  const { t } = useTranslation();
  const { data: resp, isLoading, refetch } = useQuery<{
    ok: boolean;
    error?: string;
    dry_run?: boolean;
    /** Publikované network záznamy tohoto uzlu. */
    my_entries?: number;
    /** Známá SSO client_id napříč entries. */
    known_clients?: number;
    kc_alive?: number;
    kc_acm_clients?: number;
    orphan_count?: number;
    actions?: Array<{
      action: 'create' | 'create_skip' | 'orphan_delete' | 'orphan_skip' | 'failed';
      domain?: string;
      service?: string;
      client_id?: string;
      detail: string;
    }>;
  }>({
    queryKey: ['sso-registrations'],
    queryFn: () => mcpCall('sso.reconcile', { dry_run: true }),
    staleTime: 30_000,
  });

  const reconcileMut = useMutation({
    mutationFn: () => mcpCall('sso.reconcile', { dry_run: false }),
    onSuccess: () => {
      toast.success(t('sso.reconcilerDone'));
      refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (isLoading) {
    return (
      <div className='py-8 text-center text-gray-500'>
        <Loader2 size={16} className='animate-spin inline mr-2' />{t('common.loading')}
      </div>
    );
  }

  const data = resp;
  const actions = (data?.actions ?? []) as Array<
    { action: string; domain?: string; client_id?: string; detail: string }
  >;

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
        <Card>
          <CardContent className='p-3 text-center'>
            <div className='text-2xl font-bold text-white'>{data?.my_entries ?? 0}</div>
            <div className='text-[10px] text-gray-500'>{t('sso.publishedStat')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-3 text-center'>
            <div className='text-2xl font-bold text-white'>{data?.known_clients ?? 0}</div>
            <div className='text-[10px] text-gray-500'>{t('sso.ssoRegistrationsStat')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-3 text-center'>
            <div className='text-2xl font-bold text-white'>{data?.kc_acm_clients ?? 0}</div>
            <div className='text-[10px] text-gray-500'>{t('sso.kcAcmClientsStat')}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle icon={Activity}>{t('sso.reconcilerStatusTitle')}</CardTitle>
          <div className='flex items-center gap-2'>
            <RefreshButton onRefresh={refetch} loading={isLoading} />
            <Button
              variant='primary'
              size='sm'
              loading={reconcileMut.isPending}
              onClick={() => reconcileMut.mutate()}
            >
              <RefreshCw size={12} /> {t('sso.runReconciler')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {actions.length === 0
            ? (
              <div className='py-6 text-center'>
                <CheckCircle2 size={24} className='text-emerald-400 mx-auto mb-2' />
                <p className='text-sm text-emerald-400 font-medium'>{t('sso.allSynced')}</p>
                <p className='text-xs text-gray-500'>
                  {t('sso.noDiscrepancies')}
                </p>
              </div>
            )
            : (
              <div className='space-y-2'>
                {actions.map((a, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border ${
                      a.action === 'create' || a.action === 'recreate'
                        ? 'border-blue-500/20 bg-blue-500/5'
                        : a.action === 'delete_orphan'
                        ? 'border-red-500/20 bg-red-500/5'
                        : 'border-gray-800'
                    }`}
                  >
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${
                        a.action === 'create'
                          ? 'bg-blue-500/10 text-blue-400'
                          : a.action === 'recreate'
                          ? 'bg-amber-500/10 text-amber-400'
                          : a.action === 'delete_orphan'
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-gray-800 text-gray-500'
                      }`}
                    >
                      {a.action}
                    </span>
                    {a.domain && <span className='text-xs font-mono text-white'>{a.domain}</span>}
                    {a.client_id && <span className='text-[10px] text-gray-500'>{a.client_id}
                    </span>}
                    <span className='text-[10px] text-gray-500 ml-auto'>{a.detail}</span>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function AddFirstKeycloakButton({ onAdded }: {
  tenants: Tenant[];
  myTenant: string;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant='primary' onClick={() => setOpen(true)}>
        {t('sso.addKeycloak')}
      </Button>
      {open && (
        <AddExternalKcModal
          onClose={() => setOpen(false)}
          onAdded={() => {
            setOpen(false);
            onAdded();
          }}
        />
      )}
    </>
  );
}

function AddExternalKcModal({ onClose, onAdded }: {
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState('');
  const [url, setUrl] = useState('https://');
  const [realm, setRealm] = useState('master');
  const [clientId, setClientId] = useState('admin-cli');
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [mode, setMode] = useState<'read' | 'write'>('read');

  const add = useMutation({
    mutationFn: () =>
      mcpCall<{ ok: boolean; key: string; message?: string; error?: string }>(
        'sso.add_external_instance',
        {
          slug, url, realm, client_id: clientId,
          admin_user: adminUser, admin_password: adminPass,
          mode, priority: 10, enabled: true,
        },
      ),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(r.message ?? t('sso.kcInstanceAdded', { slug, mode }));
        onAdded();
      } else {
        toast.error(r.error ?? t('sso.addFailed'));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal open onClose={onClose} title={t('sso.addExternalKcTitle')} size='lg'>
      <div className='space-y-4 p-4'>
        <Field label={t('sso.slugFormatField')} value={slug} onChange={setSlug} />
        <Field label={t('sso.urlField')} value={url} onChange={setUrl} mono />
        <Field label={t('sso.realmField')} value={realm} onChange={setRealm} />
        <Field label={t('sso.clientIdField')} value={clientId} onChange={setClientId} />
        <Field label={t('sso.adminUsernameField')} value={adminUser} onChange={setAdminUser} />
        <Field label={t('sso.adminPasswordField')} value={adminPass} onChange={setAdminPass} type='password' />

        <div className='border border-gray-800 rounded-lg p-3 bg-gray-900/30'>
          <label className='flex items-center gap-2 cursor-pointer'>
            <input
              type='checkbox'
              checked={mode === 'write'}
              onChange={(e) => setMode(e.target.checked ? 'write' : 'read')}
              className='w-4 h-4'
            />
            <span className='text-sm text-gray-200'>{t('sso.enableWriteMode')}</span>
          </label>
          {mode === 'write' && (
            <div className='mt-2 text-[11px] text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2'>
              {t('sso.writeModeWarning')}
            </div>
          )}
        </div>

        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant='primary'
            onClick={() => add.mutate()}
            loading={add.isPending}
            disabled={!slug || !url || !adminUser || !adminPass}
          >
            {t('sso.addWithMode', { mode })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Identity backends (tenant domény + AD federace + M365 brokering) ──────────
type MutResult = { ok?: boolean; error?: string } & Record<string, unknown>;
function resultToast(r: MutResult, okMsg: string, qc: ReturnType<typeof useQueryClient>, key: string) {
  if (r && r.ok === false) {
    toast.error(r.error ?? i18n.t('sso.error'));
  } else {
    toast.success(okMsg);
    qc.invalidateQueries({ queryKey: [key] });
  }
}

function IdentityTab() {
  const { t } = useTranslation();
  return (
    <div className='space-y-4'>
      <p className='text-xs text-gray-500'>
        {t('sso.identityIntro')}
      </p>
      <DomainsCard />
      <FederationCard />
      <IdpCard />
    </div>
  );
}

function DomainsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  const [primary, setPrimary] = useState(false);
  const list = useQuery({
    queryKey: ['sso-domains'],
    queryFn: () =>
      mcpCall<{ domains: Array<{ slug: string; domain: string; is_primary: boolean }> }>(
        'sso.tenant_domain_list',
        {},
      ),
  });
  const add = useMutation({
    mutationFn: () => mcpCall<MutResult>('sso.tenant_domain_add', { slug, domain, primary }),
    onSuccess: (r) => {
      resultToast(r, t('sso.domainAdded'), qc, 'sso-domains');
      if (r.ok !== false) setDomain('');
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const prim = useMutation({
    mutationFn: (d: { slug: string; domain: string }) =>
      mcpCall<MutResult>('sso.tenant_domain_set_primary', d),
    onSuccess: (r) => resultToast(r, t('sso.primarySwitched'), qc, 'sso-domains'),
    onError: (e: Error) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (d: { slug: string; domain: string }) =>
      mcpCall<MutResult>('sso.tenant_domain_remove', d),
    onSuccess: (r) => resultToast(r, t('sso.domainRemoved'), qc, 'sso-domains'),
    onError: (e: Error) => toast.error(e.message),
  });
  const rows = list.data?.domains ?? [];
  return (
    <Card>
      <CardHeader><CardTitle>{t('sso.tenantDomainsTitle')}</CardTitle></CardHeader>
      <CardContent className='space-y-3'>
        <div className='space-y-1'>
          {rows.map((d) => (
            <div
              key={d.slug + d.domain}
              className='flex items-center justify-between text-sm border border-gray-800 rounded-lg px-3 py-1.5'
            >
              <span className='font-mono'>
                {d.domain} <span className='text-gray-500'>→ {d.slug}</span>{' '}
                {d.is_primary && <Badge variant='success'>{t('sso.primaryBadge')}</Badge>}
              </span>
              <span className='flex gap-2'>
                {!d.is_primary && (
                  <Button
                    size='sm'
                    variant='ghost'
                    onClick={() => prim.mutate({ slug: d.slug, domain: d.domain })}
                  >
                    {t('sso.primaryButton')}
                  </Button>
                )}
                <Button
                  size='sm'
                  variant='ghost'
                  onClick={() => remove.mutate({ slug: d.slug, domain: d.domain })}
                >
                  {t('sso.removeButton')}
                </Button>
              </span>
            </div>
          ))}
          {rows.length === 0 && <div className='text-sm text-gray-500'>{t('sso.noDomains')}</div>}
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <Field label={t('sso.tenantSlugField')} value={slug} onChange={setSlug} mono />
          <Field label={t('sso.domainField')} value={domain} onChange={setDomain} mono />
        </div>
        <label className='flex items-center gap-2 text-sm text-gray-300'>
          <input type='checkbox' checked={primary} onChange={(e) => setPrimary(e.target.checked)} />
          {t('sso.primaryCheckbox')}
        </label>
        <Button onClick={() => add.mutate()} disabled={!slug || !domain || add.isPending}>
          {t('sso.addDomain')}
        </Button>
      </CardContent>
    </Card>
  );
}

function FederationCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [f, setF] = useState({ name: '', connectionUrl: '', bindDn: '', bindCredential: '', usersDn: '' });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const list = useQuery({
    queryKey: ['sso-federation'],
    queryFn: () =>
      mcpCall<{ federations: Array<{ name: string; connectionUrl: string; usersDn: string; editMode: string }> }>(
        'sso.ad_federation_list',
        {},
      ),
  });
  const save = useMutation({
    mutationFn: () => mcpCall<MutResult>('sso.ad_federation_set', f),
    onSuccess: (r) => resultToast(r, t('sso.adFederationSaved'), qc, 'sso-federation'),
    onError: (e: Error) => toast.error(e.message),
  });
  const test = useMutation({
    mutationFn: () =>
      mcpCall<MutResult>('sso.ad_federation_test', {
        connectionUrl: f.connectionUrl,
        bindDn: f.bindDn,
        bindCredential: f.bindCredential,
      }),
    onSuccess: (r) =>
      r.ok
        ? toast.success(t('sso.ldapTestOk'))
        : toast.error(t('sso.ldapTestFailed', { details: JSON.stringify(r.results ?? r.error) })),
    onError: (e: Error) => toast.error(e.message),
  });
  const sync = useMutation({
    mutationFn: (name: string) => mcpCall<MutResult>('sso.ad_federation_sync', { name }),
    onSuccess: (r) => resultToast(r, t('sso.syncStarted'), qc, 'sso-federation'),
    onError: (e: Error) => toast.error(e.message),
  });
  const rows = list.data?.federations ?? [];
  return (
    <Card>
      <CardHeader><CardTitle>{t('sso.adFederationTitle')}</CardTitle></CardHeader>
      <CardContent className='space-y-3'>
        <div className='space-y-1'>
          {rows.map((x) => (
            <div key={x.name} className='flex items-center justify-between text-sm border border-gray-800 rounded-lg px-3 py-1.5'>
              <span className='font-mono'>{x.name} <span className='text-gray-500'>{x.connectionUrl} · {x.editMode}</span></span>
              <Button size='sm' variant='ghost' onClick={() => sync.mutate(x.name)}>{t('sso.syncButton')}</Button>
            </div>
          ))}
          {rows.length === 0 && <div className='text-sm text-gray-500'>{t('sso.noAdFederation')}</div>}
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <Field label={t('sso.nameAliasField')} value={f.name} onChange={set('name')} mono />
          <Field label='connectionUrl' value={f.connectionUrl} onChange={set('connectionUrl')} mono />
          <Field label='bindDn' value={f.bindDn} onChange={set('bindDn')} mono />
          <Field label='bindCredential' value={f.bindCredential} onChange={set('bindCredential')} type='password' />
          <Field label='usersDn' value={f.usersDn} onChange={set('usersDn')} mono />
        </div>
        <div className='flex gap-2'>
          <Button onClick={() => save.mutate()} disabled={!f.name || !f.connectionUrl || save.isPending}>{t('common.save')}</Button>
          <Button variant='secondary' onClick={() => test.mutate()} disabled={!f.connectionUrl || test.isPending}>{t('sso.testLdapButton')}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function IdpCard() {
  const scope = { ..._ssoScope };
  const scopeKey = JSON.stringify(scope);
  return <EntraOnboardingCard key={scopeKey} scopeKey={scopeKey}
    call={(name, args) => callWidgetTool(name, args, scope)} />;
}

function Field({ label, value, onChange, type = 'text', mono = false }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className='text-[10px] uppercase tracking-wider text-gray-500 mb-1'>{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50 ${
          mono ? 'font-mono' : ''
        }`}
      />
    </div>
  );
}
